import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
    GitCompareArrows, Loader2, Sparkles, CheckCircle2, PlusCircle, AlertTriangle,
    BookMarked, Lightbulb, RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { workspacesApi, getAiLanguage } from "@/lib/apiClient";
import { useTranslation } from "@/context/LanguageContext";
import { toast } from "sonner";

const Section = ({ icon: Icon, title, accent, children }) => (
    <div className="rounded-2xl border border-border bg-surface-1 p-5">
        <div className="mb-3 flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg" style={{ background: `${accent}22`, color: accent }}><Icon className="h-4 w-4" /></div>
            <h3 className="font-sans-display text-sm font-semibold text-t1">{title}</h3>
        </div>
        {children}
    </div>
);

const SrcTags = ({ sources }) => (
    <div className="mt-1 flex flex-wrap gap-1">
        {(sources || []).map((s, i) => (
            <span key={i} className="rounded-full bg-surface-2 px-2 py-0.5 text-[10px] text-t3">{s}</span>
        ))}
    </div>
);

// P5 — Cross-Document Intelligence: what the sources agree on, where they add to
// each other, where they contradict, and which source explains what best.
export const WorkspaceInsights = ({ wsId, sourceCount }) => {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(true);
    const [running, setRunning] = useState(false);
    const enough = (sourceCount || 0) >= 2;

    useEffect(() => {
        (async () => {
            try {
                const res = await workspacesApi.getInsights(wsId, getAiLanguage());
                if (res && res.data) { setData(res.data); setMeta(res); }
            } catch (e) { /* ignore */ } finally { setLoading(false); }
        })();
    }, [wsId]);

    const analyze = useCallback(async () => {
        setRunning(true);
        try {
            const res = await workspacesApi.insights(wsId, "auto", true);
            setData(res.data); setMeta(res);
            toast.success(t("workspace.compare.done"));
        } catch (e) {
            toast.error(e?.response?.status === 400 ? t("workspace.compare.needTwo") : t("workspace.compare.failed"));
        } finally { setRunning(false); }
    }, [wsId, t]);

    if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--primary))]" /></div>;

    if (!enough) {
        return (
            <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border bg-surface-1 px-6 py-16 text-center" data-testid="workspace-compare-empty">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-surface-2 text-[hsl(var(--primary))]"><GitCompareArrows className="h-7 w-7" /></div>
                <h3 className="mt-4 font-sans-display text-lg font-semibold text-t1">{t("workspace.compare.title")}</h3>
                <p className="mt-1.5 max-w-md text-sm text-t3">{t("workspace.compare.needTwo")}</p>
            </div>
        );
    }

    const outdated = data && meta && meta.source_count != null && meta.source_count !== sourceCount;

    if (!data) {
        return (
            <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border bg-surface-1 px-6 py-16 text-center" data-testid="workspace-compare-cta">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-surface-2 text-[hsl(var(--primary))]"><GitCompareArrows className="h-7 w-7" /></div>
                <h3 className="mt-4 font-sans-display text-lg font-semibold text-t1">{t("workspace.compare.title")}</h3>
                <p className="mt-1.5 max-w-md text-sm text-t3">{t("workspace.compare.intro")}</p>
                <Button data-testid="workspace-compare-run" className="mt-5 gap-2" disabled={running} onClick={analyze}>
                    {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {t("workspace.compare.analyze")}
                </Button>
            </div>
        );
    }

    const { overview, consensus = [], complementary = [], contradictions = [], coverage = [], study_recommendation } = data;

    return (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-4" data-testid="workspace-compare">
            <div className="flex flex-col gap-3 rounded-2xl border border-border bg-surface-1 p-5 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                    <div className="flex items-center gap-2"><GitCompareArrows className="h-4 w-4 text-[hsl(var(--primary))]" /><h3 className="font-sans-display text-sm font-semibold text-t1">{t("workspace.compare.title")}</h3></div>
                    <p className="mt-1.5 text-sm text-t2">{overview}</p>
                    {outdated && <p className="mt-2 text-xs text-[hsl(38,92%,58%)]">{t("workspace.compare.outdated")}</p>}
                </div>
                <Button data-testid="workspace-compare-refresh" variant="secondary" size="sm" className="shrink-0 gap-2 bg-surface-3/60" disabled={running} onClick={analyze}>
                    {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} {t("workspace.compare.reanalyze")}
                </Button>
            </div>

            {consensus.length > 0 && (
                <Section icon={CheckCircle2} title={t("workspace.compare.consensus")} accent="hsl(152,52%,46%)">
                    <div className="space-y-3">
                        {consensus.map((c, i) => (<div key={i}><p className="text-sm text-t2">{c.point}</p><SrcTags sources={c.sources} /></div>))}
                    </div>
                </Section>
            )}

            {complementary.length > 0 && (
                <Section icon={PlusCircle} title={t("workspace.compare.complementary")} accent="hsl(206,70%,52%)">
                    <div className="space-y-3">
                        {complementary.map((c, i) => (<div key={i}><p className="text-sm font-medium text-t1">{c.point}</p>{c.detail && <p className="text-sm text-t3">{c.detail}</p>}<SrcTags sources={c.sources} /></div>))}
                    </div>
                </Section>
            )}

            <Section icon={AlertTriangle} title={t("workspace.compare.contradictions")} accent="hsl(0,72%,60%)">
                {contradictions.length === 0 ? (
                    <p className="text-sm text-t3">{t("workspace.compare.noContradictions")}</p>
                ) : (
                    <div className="space-y-4">
                        {contradictions.map((c, i) => (
                            <div key={i} className="rounded-xl border border-border bg-surface-2/50 p-3">
                                <p className="text-sm font-medium text-t1">{c.topic}</p>
                                <div className="mt-2 space-y-1.5">
                                    {(c.positions || []).map((p, j) => (
                                        <div key={j} className="flex gap-2 text-sm"><span className="shrink-0 rounded bg-surface-3 px-1.5 py-0.5 text-[10px] text-t3">{p.source}</span><span className="text-t2">{p.claim}</span></div>
                                    ))}
                                </div>
                                {c.note && <p className="mt-2 text-xs text-t3">{c.note}</p>}
                            </div>
                        ))}
                    </div>
                )}
            </Section>

            {coverage.length > 0 && (
                <Section icon={BookMarked} title={t("workspace.compare.coverage")} accent="hsl(258,70%,64%)">
                    <div className="grid gap-3 sm:grid-cols-2">
                        {coverage.map((c, i) => (
                            <div key={i} className="rounded-xl border border-border bg-surface-2/50 p-3">
                                <p className="text-sm font-semibold text-t1">{c.source}</p>
                                {c.best_for && <p className="mt-1 text-xs text-t2"><span className="text-[hsl(152,52%,46%)]">✓ </span>{c.best_for}</p>}
                                {c.strengths && <p className="mt-1 text-xs text-t3">{c.strengths}</p>}
                                {c.gaps && <p className="mt-1 text-xs text-[hsl(38,92%,58%)]">{t("workspace.compare.gaps")}: {c.gaps}</p>}
                            </div>
                        ))}
                    </div>
                </Section>
            )}

            {study_recommendation && (
                <Section icon={Lightbulb} title={t("workspace.compare.studyPlan")} accent="hsl(38,92%,58%)">
                    <p className="text-sm text-t2">{study_recommendation}</p>
                </Section>
            )}
        </motion.div>
    );
};
